import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { ReactNode, useEffect, useMemo } from 'react';

import { useDcrNavSection } from '@/core/store/dcrNavigation/dcrNavigationSelectors';
import {
	useDcrFormFilteredFields,
	useDcrFormSearchTerm
} from '@/core/store/dcrs/dcrSelectors';
import { HcoSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcoFieldNames';
import { HcpSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcpFieldNames';

import { useDcrContext } from '../../_context/DcrContext';

import { formSectionStyles as sx } from './FormSection.styles';

const simpleViewSections: Array<HcpSection | HcoSection> = [
	'name',
	'professional',
	'organizational',
	'addresses',
	'affiliations',
	'partners'
];

type Props = {
	title: string;
	id: HcoSection | HcpSection;
	children: ReactNode;
};

export const FormSection = ({ title, id, children }: Props) => {
	const { isSimpleView } = useDcrContext();
	const { addSection, setHidden, removeSection } = useDcrNavSection();
	const query = useDcrFormSearchTerm();
	const fields = useDcrFormFilteredFields(id);

	useEffect(() => {
		addSection({
			id: id ?? title,
			title: title
		});

		return () => {
			removeSection(id ?? title);
		};
	}, [id, title, addSection, removeSection]);

	const isSectionVisible = useMemo(
		() =>
			isSimpleView
				? simpleViewSections.includes(id)
				: query === '' || fields.length > 0,
		[isSimpleView, id, query, fields.length]
	);

	useEffect(() => {
		setHidden(id, !isSectionVisible);
	}, [setHidden, isSectionVisible, id]);

	return (
		<>
			{isSectionVisible && !isSimpleView && <Divider sx={sx.divider} />}

			<Box sx={sx.root(isSectionVisible)} id={`dcr-form-${id}`}>
				{!isSimpleView && (
					<Typography variant='h6' component='h2'>
						{title}
					</Typography>
				)}

				<Box sx={sx.content}>{children}</Box>
			</Box>
		</>
	);
};
